import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { isValidDate } from '@core';
import { useClubCollections } from '@queries';
import {
    activityFilterAtom,
    resetActivityFilterAtom,
    updateActivityFilterAtom,
    useEnsureAtomValue,
    userAtom,
} from '@store';

import { INITIAL_DATE_RANGE } from './useDebounceDateFilter';

export function useSharedActivityFilter() {
    const user = useEnsureAtomValue(userAtom);
    const updateFilter = useSetAtom(updateActivityFilterAtom);
    const clearFilter = useSetAtom(resetActivityFilterAtom);
    const storedFilter = useAtomValue(activityFilterAtom);
    const filter = useMemo(
        () => ({
            ...storedFilter,
            startDate: isValidDate(storedFilter.startDate) ? storedFilter.startDate : null,
            endDate: isValidDate(storedFilter.endDate) ? storedFilter.endDate : null,
        }),
        [storedFilter],
    );
    const { data: clubCollections } = useClubCollections();
    const initialStartDate = useMemo(() => INITIAL_DATE_RANGE.start, []);
    const initialEndDate = useMemo(() => INITIAL_DATE_RANGE.end, []);
    const startDate = filter.startDate ?? initialStartDate;
    const endDate = filter.endDate ?? initialEndDate;
    const [clubModalOpen, setClubModalOpen] = useState(false);
    const selectedClubGroup = useMemo(
        () => clubCollections?.find((clubGroup) => clubGroup.id === filter.clubTypeId)?.typeName,
        [clubCollections, filter.clubTypeId],
    );
    const currentClub = useMemo(
        () => clubCollections?.flatMap(({ clubs }) => clubs).find((club) => filter.clubId === club.id),
        [clubCollections, filter.clubId],
    );
    const handleKeywordChange = useCallback((keywords: string) => updateFilter({ keywords }), [updateFilter]);

    const toggleOnlyFavorites = useCallback(
        () => updateFilter({ favorites: !filter.favorites }),
        [filter.favorites, updateFilter],
    );

    const openClubModal = useCallback(() => setClubModalOpen(true), []);

    const handleClubSelect = useCallback(
        (clubId: number) => {
            updateFilter({
                clubTypeId: undefined,
                hasClub: undefined,
                clubId: filter.clubId === clubId ? undefined : clubId,
            });

            setClubModalOpen(false);
        },
        [filter.clubId, updateFilter],
    );

    const handleNoClubSelect = useCallback(() => {
        updateFilter({
            clubId: undefined,
            clubTypeId: undefined,
            hasClub: filter.hasClub === false ? undefined : false,
        });
        setClubModalOpen(false);
    }, [filter.hasClub, updateFilter]);

    const handleClubGroupSelect = useCallback(
        (clubGroupName: string, clubTypeId: number) => {
            const isCurrentlySelected = selectedClubGroup === clubGroupName;

            setClubModalOpen(false);
            updateFilter({
                clubTypeId: isCurrentlySelected ? undefined : clubTypeId,
                clubId: undefined,
                hasClub: undefined,
            });
        },
        [selectedClubGroup, updateFilter],
    );

    const clearClubSelectionUi = useCallback(() => {}, []);

    const hasFilter = useMemo(() => filter.hasClub === false || Object.values(filter).some(Boolean), [filter]);

    useEffect(() => {
        if (filter.userId !== user.id) {
            clearFilter();
        }
    }, [user.id, clearFilter, filter.userId]);

    return {
        initialStartDate,
        initialEndDate,
        startDate,
        endDate,
        clubModalOpen,
        selectedClubGroup,
        selectedClubLabel: filter.hasClub === false ? 'No Club' : selectedClubGroup ?? currentClub?.longName,
        noClubSelected: filter.hasClub === false,
        clubCollections,
        emptyFilter: !hasFilter,
        filter,
        updateFilter,
        clearFilter,
        setClubModalOpen,
        handleKeywordChange,
        toggleOnlyFavorites,
        openClubModal,
        handleClubSelect,
        handleNoClubSelect,
        handleClubGroupSelect,
        clearClubSelectionUi,
    };
}
